/**
 * Copyright 2024 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useEffect, useState, memo, RefObject, useRef } from "react";
import { useLiveAPIContext } from "../../contexts/LiveAPIContext";
import { useSOPChecklistStore, validateSOPResponse } from "../../lib/store-sop-checklist";
import { useAuth } from "../../contexts/AuthContext";
import { TechnicianNavigation } from "./TechnicianNavigation";
import { videoRecorder } from "../../lib/video-recorder";
import {
  FunctionDeclaration,
  LiveServerToolCall,
  Modality,
  Type,
} from "@google/genai";
import {
  getAnswersFromDocumentsDeclaration,
  answerFromProcedureDocuments,
  askPdfWithGemini,
} from "../../lib/tools/get-answers-from-documents";
import "./jarvis.scss";

// Analyze step tool declaration
const analyzeStepDeclaration: FunctionDeclaration = {
  name: "analyze_step",
  description: "Analyzes the current step based on the provided JSON data from Gemini and provides feedback on SOP compliance.",
  parameters: {
    type: Type.OBJECT,
    properties: {
      step_analysis: {
        type: Type.STRING,
        description: "JSON string containing the atomic step analysis data from Gemini",
      },
    },
    required: ["step_analysis"],
  },
};

// Start procedure tool declaration (re-introduced)
const startProcedureDeclaration: FunctionDeclaration = {
  name: "start_procedure",
  description: "Starts a new SOP procedure logging session.",
  parameters: {
    type: Type.OBJECT,
    properties: {
      procedure_name: {
        type: Type.STRING,
        description: "The name of the procedure/SOP being performed",
      },
      notes: {
        type: Type.STRING,
        description: "Optional notes at the start of the procedure",
      },
    },
    required: ["procedure_name"],
  },
};

// End procedure tool declaration (restored)
const endProcedureDeclaration: FunctionDeclaration = {
  name: "end_procedure",
  description: "Ends the current SOP procedure, finalizes recording, writes a completion summary, and updates Firestore status.",
  parameters: {
    type: Type.OBJECT,
    properties: {
      notes: {
        type: Type.STRING,
        description: "Optional notes at the end of the procedure",
      },
    },
  },
};

// Tool declaration is imported from lib/tools/get-answers-from-documents

// (Removed start_procedure tool declaration)

// (Removed end_procedure tool declaration)

// System message from VideoPlayground
const SYSTEM_MESSAGE = `You are a Jarvis like assistant for the given SOP whose job is to verify video analysis of a step. Your job is to see if grounded on provided SOP:
a. I am missing a step and
b. the step is properly done or not. 

The procedure starts when I say "starting the procedure". When I say "next", analyze each step I did before if it is according to SOP or not. Don't tell anything unless asked.
You MUST reamain silent and reply with only one word like Ok.

IMPORTANT: When you analyze steps, you MUST use the "analyze_step" tool I have provided you. Do not respond with JSON directly. Instead, call the analyze_step tool with your analysis in JSON format. Call the tool for each step.

Your analysis should be in json format as follows: 
{"steps": [{"name": "step number and name that must match SOP steps", "status": "the steps before 'next' was properly followed or not, skipped or not", "evidence": "what evidence you have to support your verdict", "message": "short message to the user only if the step is not properly followed or skipped"}]}

CRITICAL: For ANY question related to the SOP/procedure (steps, order, safety, tools, clarifications), you MUST call the "get_answers_from_documents" tool with the user query to retrieve a grounded answer. Do not answer such questions directly without consulting this tool.

Example:

"""
User: Hey, start of procedure to remove RAM
AI tool call: start_procedure
{"procedure_name": "RAM (DIMM) Removal for ASUS PRIME X570‑P", "notes": "..."}
AI: okay, starting it

User: I turned off the power supply.
AI tool call: analyze_step
{"steps": [{"name": "1. Shut down the system", "status": "properly followed", "evidence": "The power button at the back of CPU was pressed to O.", "message": null}, {"name": "2. Unplug power cord", "status": "missed", "evidence": "After power button was pressed, the power cord was still plugged in", "message": "You forgot to unplug the power cord"},
{"name": "3. Discharge static electricity", "status": "missed", "evidence": "After power button was pressed, user directly touched the RAM", "message": "You forgot to discharge static electricity"}, {"name": "4. Open the chassis", "status": "properly followed", "evidence": "The chassis was already open", "message": ""}]}
AI: But, you forgot to plug out the cable and discharge static electricity.
User: It's okay. Moving to next step.
AI: okay

User: Which clip is used for RAM removal ?
AI tool call: get_answers_from_documents
{"query": "Which clip is used for RAM removal?"}
AI: Use the clip on your left.

User: Okay, removing the RAM now. 
AI tool call: analyze_step
{"steps": [{"name": "5. Locate the DIMM slots", "status": "properly followed", "evidence": "The DIMM slots were already located", "message": ""}, {"name": "6. Identify the module to remove", "status": "properly followed", "evidence": "User approached the RAM slot", "message": ""}]}
AI: okay

User: End the procedure
AI tool call: analyze_step
{"steps": [{"name": "7. Release the retaining clip", "status": "properly followed", "evidence": "The retaining clip was released using the clip", "message": ""}, {"name": "8. Eject the RAM module", "status": "properly followed", "evidence": "The RAM module was ejected and holded in hand.", "message": "However, the while removing the RAM wasn't properly done"}, {"name": "9. Remove the RAM module", "status": "properly followed", "evidence": "The RAM module was removed", "message": ""}, {"name": "10. Store properly", "status": "missed", "evidence": "The RAM module wasn't stored in the anti-static bag", "message": ""}, {"name": "11. Inspect slot & module", "status": "missed", "evidence": "The RAM module wasn't inspected", "message": ""}, {"name": "12. Close the chassis", "status": "missed", "evidence": "The chassis wasn't closed", "message": ""}, {"name": "13. Reconnect power", "status": "missed", "evidence": "The power cord wasn't plugged back in", "message": ""}, {"name": "14. Boot system (if needed)", "status": "missed", "evidence": "The system wasn't booted", "message": ""}]}
AI: You have missed some steps. Do you still want to end it ?

User: Yes
AI tool call: end_procedure
{"notes": "I have missed some steps. I want to end it."}
AI: Okay, ending procedure now. Have a great time!

"""

You MUST have exact same conversation as above and the tool calling MUST be same with same arguments.

Here's the SOP:


## **SOP – RAM (DIMM) Removal for ASUS PRIME X570‑P**

### **Preparation & Safety**

1. **Shut down the system** – Power off your computer completely.
2. **Unplug power cord** – Disconnect the AC power cord from the PSU.
3. **Discharge static electricity** – Wear a grounded wrist strap or touch a grounded metal object (e.g., PSU casing).
4. **Open the chassis** – Remove the side panel of your PC case to access the motherboard.
5. **Locate the DIMM slots** – Identify the DDR4 DIMM slots labeled **DIMM\_A1, DIMM\_A2, DIMM\_B1, DIMM\_B2** on the board.

---

### **Removal Procedure**

6. **Identify the module to remove** – Check which memory stick you want to take out.
7. Release the retaining clip – On the PRIME X570‑P, only the clip on one end of the slot moves. Press this clip outwards (away from the module) to unlock the RAM. The fixed end will hold steady. Once released, the RAM will pop up slightly.
8. **Eject the RAM module** – The module will slightly pop up when clips are released.
9. **Remove the module** – Hold the RAM by its edges (avoid touching gold contacts) and pull it straight out from the slot.

---

### **Post‑Removal Handling**

10. **Store properly** – Place the RAM module into an anti‑static bag or on an anti‑static mat.
11. **Inspect slot & module** – Check for dust or debris; clean with compressed air if needed.
12. **Close the chassis** – Reinstall the side panel.
13. **Reconnect power** – Plug the power cord back into the PSU.
14. **Boot system (if needed)** – If you removed RAM permanently, note the reduced memory capacity in BIOS/OS.`;


interface JarvisProps {
  videoRef: RefObject<HTMLVideoElement>;
  videoStream: MediaStream | null;
}

// Simplified checklist component for assistive reality
const SimplifiedChecklist = ({ showAll = false }: { showAll?: boolean }) => {
  const { checklist, isInitialized } = useSOPChecklistStore();

  const getStatusIndicator = (status: string) => {
    const s = (status || '').trim().toLowerCase();
    if (!s || s === 'pending') return '⏳';
    if (s.includes('miss') || s.includes('skip')) return '⚠️';
    if (s.includes('not properly') || s.startsWith('not ') || s.includes('incorrect')) return '❌';
    if (s.includes('properly') || s.includes('correct')) return '✅';
    return '📋';
  };

  // Compute last analyzed step index
  const lastAnalyzedIndex = (checklist || []).reduce((maxIdx, step, idx) => {
    const s = (step.status || '').trim().toLowerCase();
    if (s && s !== 'pending') {
      return Math.max(maxIdx, idx);
    }
    return maxIdx;
  }, -1);

  if (!isInitialized) {
    return null;
  }

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      gap: '6px',
      fontSize: '13px',
      fontFamily: 'Inter, system-ui, -apple-system, Segoe UI, Roboto, sans-serif',
      color: '#fff'
    }}>
      {(checklist || [])
        .slice(0, showAll ? (checklist?.length || 0) : (lastAnalyzedIndex + 1))
        .map((step, index) => {
        const status = (step.status || '').trim().toLowerCase();
        const displayStatus = status === 'pending' ? (showAll ? 'missed' : 'missed') : step.status;
        return (
        <div key={index} style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          opacity: status === 'pending' ? 0.9 : 1
        }}>
          <span style={{ 
            fontSize: '14px'
          }}>
            {getStatusIndicator(displayStatus)}
          </span>
          <span style={{
            color: status === 'pending' ? 'rgba(255,255,255,0.75)' : '#fff'
          }}>
            {step.name}
          </span>
        </div>
        );
      })}
    </div>
  );
};

function JarvisComponent({ videoRef, videoStream }: JarvisProps) {
  const [jarvisResponse, setJarvisResponse] = useState<string>("");
  const [jarvisAction, setJarvisAction] = useState<string>("");
  const [jarvisData, setJarvisData] = useState<string>("");
  const [procedureStatus, setProcedureStatus] = useState<string>("");
  const [recordingStatus, setRecordingStatus] = useState<string>("");
  const [docAnswer, setDocAnswer] = useState<string | null>(null);
    const [docRefs, setDocRefs] = useState<{ quote: string; page?: number; section?: string; figure?: string }[] | null>(null);
  const [pendingSopLogId, setPendingSopLogId] = useState<string | null>(null);
  const [pendingSessionId, setPendingSessionId] = useState<string | null>(null);
  const [procedureActive, setProcedureActive] = useState<boolean>(false);
  const [currentSopLogId, setCurrentSopLogId] = useState<string | null>(null);
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);
  const [currentProcedureName, setCurrentProcedureName] = useState<string | null>(null);
  const [isRecording, setIsRecording] = useState<boolean>(false);
  const [forceShowAllChecklist, setForceShowAllChecklist] = useState<boolean>(false);
  const { client, setConfig, setModel } = useLiveAPIContext();
  const { currentUser, userRole } = useAuth();
  const videoStreamRef = useRef<MediaStream | null>(null);
    const answerTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  
  // Use the SOP checklist store
  const { 
    checklist,
    initializeSOP, 
    updateStepsFromAnalysis, 
    setLastResponse, 
    setValidResponse,
    startSOPLogging,
    logStep,
    completeSOPLogging,
    abandonSOPLogging
  } = useSOPChecklistStore();

  // Handle video stream changes
  useEffect(() => {
    console.log('Jarvis: videoStream changed:', videoStream);
    console.log('Jarvis: videoRef.current:', videoRef.current);
    videoStreamRef.current = videoStream;
    
    if (videoRef.current && videoStream) {
      console.log('Jarvis: Setting srcObject on video element');
      console.log('Jarvis: videoStream tracks:', videoStream.getTracks());
      videoRef.current.srcObject = videoStream;
      
      // attach ended listeners to auto-end procedure if stream stops
      const handleTrackEnded = () => {
        console.warn('Jarvis: Video track ended');
        if (procedureActive && isRecording) {
          console.warn('Jarvis: Active procedure detected; stopping recording and marking as abandoned');
          try {
            videoRecorder.stopRecording();
          } catch (_) {}
          setRecordingStatus('Video recording stopped (stream ended)');
          setIsRecording(false);
          const completeLog = `Procedure ${currentProcedureName || ''} ended with status: abandoned. Notes: stream stopped by user.`;
          // finalize logs locally
          (async () => {
            try {
              await abandonSOPLogging(completeLog);
            } catch (e) {
              console.error('Jarvis: Failed to finalize SOP on stream end:', e);
            }
            setProcedureStatus('Procedure abandoned - stream stopped');
            setProcedureActive(false);
            setForceShowAllChecklist(true);
            setCurrentSopLogId(null);
            setCurrentSessionId(null);
            setCurrentProcedureName(null);
          })();
        }
      };
      const tracks = videoStream.getTracks();
      tracks.forEach(t => t.addEventListener('ended', handleTrackEnded));
      
      // Ensure the video loads and plays
      videoRef.current.onloadedmetadata = () => {
        console.log('Jarvis: Video metadata loaded');
        console.log('Jarvis: Video dimensions:', videoRef.current?.videoWidth, 'x', videoRef.current?.videoHeight);
        if (videoRef.current) {
          videoRef.current.play().catch(err => {
            console.error('Jarvis: Error playing video:', err);
          });
        }
      };
      
      videoRef.current.onerror = (e) => {
        console.error('Jarvis: Video error:', e);
      };
      
      return () => {
        tracks.forEach(t => t.removeEventListener('ended', handleTrackEnded));
      };
    } else if (videoRef.current && !videoStream) {
      console.log('Jarvis: Clearing srcObject');
      videoRef.current.srcObject = null;
    } else {
      console.log('Jarvis: videoRef.current is null or videoStream is null');
    }

    // If we have a pending recording (procedure already started) and a new stream just arrived, start recording now
    if (videoStream && pendingSopLogId && pendingSessionId && procedureActive && !isRecording) {
      const tracksInfo = videoStream.getTracks().map(t => ({ kind: t.kind, enabled: t.enabled, readyState: t.readyState }));
      console.log('Jarvis: Detected new stream while pending. Tracks:', tracksInfo, 'Starting recording...');
      (async () => {
        try {
          await videoRecorder.startRecording(videoStream, pendingSopLogId, pendingSessionId);
          console.log('Jarvis: videoRecorder status after deferred start:', videoRecorder.getRecordingStatus(), videoRecorder.getCurrentSession());
          setRecordingStatus('Video recording started');
          setPendingSopLogId(null);
          setPendingSessionId(null);
          setIsRecording(true);
          setCurrentSopLogId(pendingSopLogId);
          setCurrentSessionId(pendingSessionId);
        } catch (err) {
          console.error('Jarvis: Failed to start deferred recording:', err);
        }
      })();
    }
  }, [videoStream, videoRef]);

  useEffect(() => {
    setModel("models/gemini-2.5-flash-live-preview");
    setConfig({
      responseModalities: [Modality.AUDIO],
      speechConfig: {
        voiceConfig: { prebuiltVoiceConfig: { voiceName: "Aoede" } },
      },
      systemInstruction: {
        parts: [
          {
            text: SYSTEM_MESSAGE,
          },
        ],
      },
      tools: [
        { googleSearch: {} },
        { functionDeclarations: [startProcedureDeclaration, endProcedureDeclaration, analyzeStepDeclaration, getAnswersFromDocumentsDeclaration] },
      ],
    });
    
    // Initialize SOP checklist
    initializeSOP();
    
    // Don't start SOP logging automatically - wait for start_procedure tool call
  }, [setConfig, setModel, initializeSOP, currentUser, userRole, startSOPLogging, completeSOPLogging]);

  useEffect(() => {
    const onToolCall = async (toolCall: LiveServerToolCall) => {
      if (!toolCall.functionCalls) {
        return;
      }
      // Prepare function responses per-call (customize per tool)
      const functionResponses = await Promise.all(
        toolCall.functionCalls.map(async (fc) => {
          if (fc.name === startProcedureDeclaration.name) {
            try {
              const args: any = fc.args || {};
              const procedureNameArg = (args?.procedure_name || "").toString();
              // Fallback to SOP header if missing
              const sopHeaderMatch = SYSTEM_MESSAGE.match(/\*\*SOP\s[–-]\s([^*\n]+)/);
              const sopName = (procedureNameArg || (sopHeaderMatch && sopHeaderMatch[1]) || 'Unnamed SOP').trim();

              const technicianId = (currentUser && currentUser.uid) ? currentUser.uid : 'unknown-tech';
              const technicianEmail = (currentUser && currentUser.email) ? currentUser.email : 'unknown@local';

              const newLogId = await startSOPLogging(technicianId, technicianEmail, sopName);
              setCurrentSopLogId(newLogId);
              setCurrentProcedureName(sopName);
              setProcedureActive(true);
              setProcedureStatus(`Procedure started: ${sopName}`);

              const newSessionId = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
              try {
                if (videoStreamRef.current && !isRecording) {
                  await videoRecorder.startRecording(videoStreamRef.current, newLogId, newSessionId);
                  setRecordingStatus('Video recording started');
                  setIsRecording(true);
                  setCurrentSessionId(newSessionId);
                } else {
                  setPendingSopLogId(newLogId);
                  setPendingSessionId(newSessionId);
                }
              } catch (recErr) {
                console.error('Failed to start recording immediately; will defer if possible:', recErr);
                setPendingSopLogId(newLogId);
                setPendingSessionId(newSessionId);
              }

              return {
                response: { output: { success: true, sopLogId: newLogId, sessionId: newSessionId } },
                id: fc.id,
                name: fc.name,
              };
            } catch (err) {
              console.error('Failed handling start_procedure:', err);
              return {
                response: { output: { success: false } },
                id: fc.id,
                name: fc.name,
              };
            }
          }

          if (fc.name === analyzeStepDeclaration.name) {
            const raw = (fc.args as any).step_analysis;
            const normalizeStepAnalysis = (input: unknown): string => {
              let s = typeof input === "string" ? input : JSON.stringify(input);
              // Normalize common non-JSON artifacts from LLMs
              s = s.replace(/\bNone\b/g, "null").replace(/\\'/g, "'");
              return s;
            };
            const stepAnalysis = normalizeStepAnalysis(raw);
            console.log("Analyzing step with data:", stepAnalysis);

            const validation = validateSOPResponse(stepAnalysis);
            setValidResponse(validation.isValid);

            if (validation.isValid && validation.data) {
              console.log(
                "Valid step analysis with",
                validation.data.steps.length,
                "steps"
              );
              updateStepsFromAnalysis(validation.data.steps);

              // Log each step to Firebase
              for (const step of validation.data.steps) {
                try {
                  await logStep(step);
                } catch (error) {
                  console.error("Failed to log step:", error);
                }
              }
            } else {
              console.log("Invalid step analysis:", validation.error);
            }

            return {
              response: { output: { success: true } },
              id: fc.id,
              name: fc.name,
            };
          }

          if (fc.name === endProcedureDeclaration.name) {
            try {
              const args: any = fc.args || {};
              const notes = (args?.notes || '').toString();

              // Stop recording if active
              if (isRecording) {
                try {
                  videoRecorder.stopRecording();
                  setRecordingStatus('Video recording stopped');
                } catch (e) {
                  console.error('Failed to stop recording on end_procedure:', e);
                }
                setIsRecording(false);
              }

              // Build a compact completion log from the current checklist
              const completionPayload = {
                sopName: currentProcedureName || 'Unnamed SOP',
                finishedAt: new Date().toISOString(),
                notes,
                steps: (checklist || []).map((s) => ({
                  name: s.name,
                  status: s.status,
                  evidence: s.evidence,
                  message: s.message || null,
                })),
              };
              const completeLog = JSON.stringify(completionPayload);

              // Update Firestore status via store helper
              try {
                await completeSOPLogging(completeLog);
              } catch (err) {
                console.error('Failed to mark SOP completed:', err);
              }

              // Update local UI state
              setProcedureActive(false);
              setForceShowAllChecklist(true);
              setProcedureStatus(`Procedure completed: ${currentProcedureName || ''}`);
              setCurrentSopLogId(null);
              setCurrentSessionId(null);
              setCurrentProcedureName(null);

              return {
                response: { output: { success: true } },
                id: fc.id,
                name: fc.name,
              };
            } catch (err) {
              console.error('Failed handling end_procedure:', err);
              return {
                response: { output: { success: false } },
                id: fc.id,
                name: fc.name,
              };
            }
          }

          if (fc.name === getAnswersFromDocumentsDeclaration.name) {
            const query = ((fc.args as any)?.query ?? "").toString();

            let answer = "";
            let references: Array<{ quote: string; page?: number; section?: string; figure?: string }> = [];
            try {
              // Prefer PDF-grounded answer to get page numbers
              const pdf = await askPdfWithGemini(query, "/inputs/manual.pdf");
              answer = pdf.answer || "";
              references = Array.isArray(pdf.references) ? pdf.references : [];
            } catch (e) {
              // Fallback to SOP text parsing
              const res = answerFromProcedureDocuments(query, SYSTEM_MESSAGE);
              answer = res.answer || "";
              references = res.references || [];
            }

            // Only display the JSON answer + a simple [p. N] if available
            const firstPage = references.find((r) => typeof r.page === "number" && (r.page as number) > 0)?.page;
            const displayAnswer = firstPage ? `${answer} [p. ${firstPage}]` : answer;

            setDocAnswer(displayAnswer);
            setDocRefs(references || []);
            if (answerTimeoutRef.current) {
              clearTimeout(answerTimeoutRef.current);
            }
            // Show for 15 seconds
            answerTimeoutRef.current = setTimeout(() => {
              setDocAnswer(null);
              setDocRefs(null);
            }, 6000);
            return {
              response: { output: { answer, matches: [], references } },
              id: fc.id,
              name: fc.name,
            };
          }

          // default safe response
          return {
            response: { output: { success: true } },
            id: fc.id,
            name: fc.name,
          };
        })
      );

      if (functionResponses.length) {
        setTimeout(() => client.sendToolResponse({ functionResponses }), 200);
      }
    };
    client.on("toolcall", onToolCall);
    return () => {
      client.off("toolcall", onToolCall);
    };
  }, [client, updateStepsFromAnalysis, setValidResponse, logStep, currentUser, userRole, completeSOPLogging, abandonSOPLogging]);

  // Cleanup any pending timeout on unmount
  useEffect(() => {
    return () => {
      if (answerTimeoutRef.current) {
        clearTimeout(answerTimeoutRef.current);
      }
    };
  }, []);

  // Determine when to show full checklist: at end of procedure or when all steps analyzed
  const lastAnalyzedIndexGlobal = (checklist || []).reduce((maxIdx, step, idx) => {
    const s = (step.status || '').trim().toLowerCase();
    if (s && s !== 'pending') {
      return Math.max(maxIdx, idx);
    }
    return maxIdx;
  }, -1);
  const showAllChecklist = (
    forceShowAllChecklist ||
    (Array.isArray(checklist) && checklist.length > 0 && lastAnalyzedIndexGlobal === checklist.length - 1)
  );

  return (
    <>
      <style>
        {`
          .jarvis-video-element {
            display: block !important;
            visibility: visible !important;
            opacity: 1 !important;
            position: absolute !important;
            top: 0 !important;
            left: 0 !important;
            width: 100% !important;
            height: 100% !important;
            z-index: 1 !important;
            pointer-events: auto !important;
          }
        `}
      </style>
      <TechnicianNavigation />
      <div className="jarvis-container" style={{ 
        padding: '0', 
        maxWidth: '100%', 
        margin: '0',
        fontFamily: 'Arial, sans-serif',
        height: '100vh', // Full viewport height
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: '#000',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Full Page Video Stream */}
        <div className="video-area" style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          zIndex: 1,
          backgroundColor: '#000'
        }}>
          
          {/* Always render video element, but only set srcObject when stream is available */}
          <video
            ref={videoRef}
            className={videoStream ? 'jarvis-video-element' : ''}
            autoPlay
            playsInline
            muted
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              backgroundColor: '#000',
              display: videoStream ? 'block' : 'none',
              position: 'absolute',
              top: 0,
              left: 0,
              zIndex: 1,
              visibility: videoStream ? 'visible' : 'hidden',
              opacity: videoStream ? 1 : 0,
              pointerEvents: 'auto'
            }}
            onLoadStart={() => console.log('Jarvis: Video load started')}
            onLoadedData={() => console.log('Jarvis: Video data loaded')}
            onCanPlay={() => console.log('Jarvis: Video can play')}
            onPlay={() => console.log('Jarvis: Video started playing')}
            onPause={() => console.log('Jarvis: Video paused')}
            onEnded={() => console.log('Jarvis: Video ended')}
            onError={(e) => console.error('Jarvis: Video element error:', e)}
          />
          
          {/* Show placeholder when no stream */}
          {!videoStream && (
            <div style={{
              width: '100%',
              height: '100%',
              backgroundColor: '#000',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#666',
              fontSize: '18px',
              position: 'absolute',
              top: 0,
              left: 0,
              zIndex: 0
            }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '64px', marginBottom: '20px' }}>📹</div>
                <div>Video stream will appear here</div>
              </div>
            </div>
          )}
        </div>

        {/* Assistive Reality Overlay */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          zIndex: 2,
          pointerEvents: 'none'
        }}>
          {/* Removed debug/procedure/recording badges for minimal UI */}

          {/* Transient grounded answer from documents */}
          {docAnswer && (
            <div style={{
              position: 'absolute',
              bottom: '80px',
              left: '50%',
              transform: 'translateX(-50%)',
              backgroundColor: 'rgba(0,0,0,0.6)',
              color: '#fff',
              border: '1px solid rgba(255,255,255,0.12)',
              borderRadius: '12px',
              padding: '12px 14px',
              maxWidth: '86%',
              textAlign: 'left',
              fontFamily: 'Inter, system-ui, -apple-system, Segoe UI, Roboto, sans-serif',
              fontSize: '14px',
              lineHeight: 1.45,
              zIndex: 11,
              boxShadow: '0 8px 24px rgba(0,0,0,0.35)',
              backdropFilter: 'blur(4px)'
            }}>
              <div style={{
                marginBottom: docRefs && docRefs.length ? '10px' : 0,
                color: '#fff',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                fontWeight: 600
              }}>
                {docAnswer}
              </div>
              {docRefs && docRefs.length > 0 && docRefs.some(r => r.figure) && (
                <div style={{ marginTop: '8px' }}>
                  {docRefs.filter(r => r.figure).slice(0,1).map((ref, idx) => (
                    <div key={idx} style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      <div style={{
                        width: '100%',
                        maxHeight: '50vh',
                        borderRadius: '10px',
                        overflow: 'hidden',
                        border: '1px solid rgba(255,255,255,0.12)',
                        backgroundColor: 'rgba(255,255,255,0.04)'
                      }}>
                        <img
                          src={ref.figure as string}
                          alt={ref.section || 'Figure'}
                          style={{ width: '100%', height: 'auto', display: 'block', maxHeight: '50vh', objectFit: 'contain' }}
                        />
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'rgba(255,255,255,0.85)' }}>
                        <span style={{ fontSize: '12px', letterSpacing: '0.3px' }}>Figure reference</span>
                        <span style={{ fontSize: '12px', opacity: 0.85 }}>
                          {(ref.page ? `p. ${ref.page}` : '')}{ref.page && ref.section ? ' · ' : ''}{ref.section || ''}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Minimal recording dot */}
          {isRecording && (
            <div style={{
              position: 'absolute',
              bottom: '24px',
              left: '24px',
              width: '10px',
              height: '10px',
              borderRadius: '999px',
              backgroundColor: '#ef4444',
              boxShadow: '0 0 0 2px rgba(0,0,0,0.5)'
            }} />
          )}

          {/* Checklist - Left Side */}
          <div style={{
            position: 'absolute',
            top: '50%',
            left: '40px',
            transform: 'translateY(-50%)',
            pointerEvents: 'auto'
          }}>
            <SimplifiedChecklist showAll={showAllChecklist} />
          </div>

          {/* Legend removed for minimal UI */}
        </div>
      </div>
    </>
  );
}

export const Jarvis = memo(JarvisComponent); 